import Component from '@cell/cell-core/component/component';
import tracker from '@cell/cell-core/tracker';
import './product-quiz.scss';
import { normalizePathname } from './utils';

const SESSION_STORAGE_KEY = 'product-quiz-result-path';

class ProductQuiz extends Component {
  constructor() {
    super();

    this.getData();

    this.currentQuestionIndex = 0;
    this.path = [];
  }

  componentDidMount() {
    this.setEventListeners();

    const entry = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!entry) {
      tracker.allV2('view_product_quiz');

      this.renderStart();

      return;
    }

    const parsedEntry = JSON.parse(entry);
    const path = parsedEntry[normalizePathname(window.location.pathname)];

    if (path) {
      this.path = path;

      tracker.allV2('view_product_quiz_result');

      this.renderResult();
    } else {
      tracker.allV2('view_product_quiz');

      this.renderStart();
    }
  }

  getData() {
    try {
      const { questions, results } = JSON.parse(
        this.querySelector('script[type="application/json"]').innerHTML,
      );

      this.questions = questions;
      this.results = results;
    } catch (error) {
      throw new Error('Could not parse JSON data');
    }
  }

  setEventListeners() {
    this.on('c-button.click.start-quiz', this.handleQuizStart.bind(this));
    this.on('c-button.click.restart-quiz', this.handleQuizRestart.bind(this));

    this.on('c-button.click.prev-question', this.handlePrevQuestion.bind(this));
    this.on('c-button.click.next-question', this.handleNextQuestion.bind(this));
  }

  handleQuizStart() {
    tracker.allV2('start_product_quiz');

    this.renderQuestion();
  }

  handleQuizRestart() {
    tracker.allV2('restart_product_quiz');

    // Reset state.
    this.currentQuestionIndex = 0;
    this.path = [];

    // Show the quiz.
    this.querySelector('.ProductQuizMain').classList.add('ProductQuizMain--Active');

    // Hide everything related to the result.
    this.querySelector('.ProductQuizResult').classList.remove('ProductQuizResult--Active');
    this.querySelector('.ProductQuizRestart').classList.remove('ProductQuizRestart--Active');
    this.querySelector('.ProductQuizExploreAllProducts')?.classList.remove(
      'ProductQuizExploreAllProducts--Active',
    );

    // Render the first question.
    this.renderQuestion();

    // Make sure the quiz is fully visible after restarting.
    this.scrollToTop();
  }

  handlePrevQuestion() {
    this.currentQuestionIndex = this.currentQuestionIndex - 1;

    if (this.currentQuestionIndex < this.questions.length) {
      this.renderQuestion();
    } else {
      // This case should not happen since the previous button is hidden on the first question.
      throw new Error('Unexpected error: Could not render previous question');
    }
  }

  handleNextQuestion() {
    this.currentQuestionIndex = this.currentQuestionIndex + 1;

    if (this.currentQuestionIndex < this.questions.length) {
      this.renderQuestion();
    } else {
      this.handleQuizEnd();
    }
  }

  handleQuizEnd() {
    tracker.allV2('submit_product_quiz');

    const currentEntry = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!currentEntry) {
      const newEntry = {
        [normalizePathname(window.location.pathname)]: this.path,
      };

      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newEntry));
    } else {
      const currentEntryParsed = JSON.parse(currentEntry);

      const newEntry = {
        ...currentEntryParsed,
        [normalizePathname(window.location.pathname)]: this.path,
      };

      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newEntry));
    }

    this.renderResult();

    // Scroll to the top of the quiz to make sure the result is fully visible.
    this.scrollToTop();
  }

  handleAnswerSelect(event) {
    const questionIndex = this.currentQuestionIndex;
    const answerIndex = event.target.value;

    this.path[questionIndex] = answerIndex;

    // The next button is disabled by default. It is enabled when an answer is selected.
    this.querySelector('c-button[data-action="next-question"] button').removeAttribute('disabled');
  }

  renderStart() {
    const startTemplateElement = this.querySelector('template[data-quiz-start-template]');
    const startTemplateElementClone = startTemplateElement.content.cloneNode(true);

    this.querySelector('.ProductQuiz__Container').replaceChildren(startTemplateElementClone);
  }

  renderQuestion() {
    const questionIndex = this.currentQuestionIndex;

    const questionTemplateElement = this.querySelector('template[data-quiz-question-template]');
    const questionTemplateElementClone = questionTemplateElement.content.cloneNode(true);

    const { question, description, answers } = this.questions[questionIndex];

    tracker.allV2('view_product_quiz_question', {
      index: questionIndex,
      content: question,
    });

    questionTemplateElementClone.querySelector('.Heading').innerHTML = question;
    questionTemplateElementClone.querySelector('.Text').innerHTML = description;

    questionTemplateElementClone
      .querySelectorAll('.ProductQuiz__Answer')
      .forEach((answerButtonElement, answerButtonElementIndex) => {
        const answer = answers[answerButtonElementIndex];

        if (answer) {
          answerButtonElement.querySelector('label').innerHTML = answer;

          if (this.path[questionIndex] === `${answerButtonElementIndex}`) {
            answerButtonElement.querySelector('input').setAttribute('checked', 'checked');

            // The next button is disabled by default. It is enabled when an answer is selected.
            questionTemplateElementClone
              .querySelector('c-button[data-action="next-question"] button')
              .removeAttribute('disabled');
          }
        } else {
          answerButtonElement.remove();
        }
      });

    if (questionIndex === 0) {
      questionTemplateElementClone.querySelector(
        'c-button[data-action="prev-question"]',
      ).style.visibility = 'hidden';
    }

    this.querySelector('.ProductQuiz__Container').replaceChildren(questionTemplateElementClone);

    this.querySelectorAll('.ProductQuiz__Answer').forEach((answerButtonElement) => {
      answerButtonElement.addEventListener('change', this.handleAnswerSelect.bind(this));
    });
  }

  renderResult() {
    const result = this.results.find(({ paths }) => paths.includes(this.path.join('.')));

    const { product } = result;

    // Populate collection banner.
    this.querySelector('.CollectionBanner .Image__Image').src = product.image;
    this.querySelector('.CollectionBanner .Heading').innerHTML = product.heading;
    this.querySelector('.CollectionBanner .Text').innerHTML = product.text;

    // Populate reasons and link product.
    this.querySelectorAll('.ProductQuizResult__Reasons ul li').forEach(
      (listItemElement, listItemElementIndex) => {
        const benefit = product.benefits[listItemElementIndex];

        if (!benefit) {
          listItemElement.remove();

          return;
        }

        listItemElement.querySelector('span span').textContent = benefit;
      },
    );

    this.querySelector('.ProductQuizResult__Reasons .Button--Primary a').href = product.url;

    // Hide the quiz.
    this.querySelector('.ProductQuizMain').classList.remove('ProductQuizMain--Active');

    // Show everything related to the result.
    this.querySelector('.ProductQuizResult').classList.add('ProductQuizResult--Active');
    this.querySelector('.ProductQuizRestart').classList.add('ProductQuizRestart--Active');
    this.querySelector('.ProductQuizExploreAllProducts')?.classList.add(
      'ProductQuizExploreAllProducts--Active',
    );
  }

  scrollToTop() {
    let scrollOffset = window.scrollY;

    // If the site header is sticky, we need to subtract its height from the scroll offset.

    const siteHeaderElement = document.querySelector('site-header');

    if (siteHeaderElement && siteHeaderElement.classList.contains('SiteHeader--Sticky')) {
      const siteHeaderHeight = siteHeaderElement.getBoundingClientRect().height;

      scrollOffset -= siteHeaderHeight;
    }

    globalThis.scrollTo({
      top: this.getBoundingClientRect().top + scrollOffset,
      behavior: 'smooth',
    });
  }
}

customElements.define('product-quiz', ProductQuiz);
